import { EMEARegion, NARegion } from "@dana/Common/Constants";
import { formatDateWithDash } from "@dana/Common/utils";
import InvoiceRecepients from "@dana/Components/OrderHistory/InvoiceRecepients";
import { PrintButton } from "@dana/Components/PrintButton/PrintButton";
import StyledTable from "@dana/Components/StyledTable/StyledTable";
import XlsDownload from "@dana/Icons/XlsDownload";
import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { SalseOrgDetails } from "@dana/Services/StandardReturnDetailsService";
import { InvoiceDetailsResponse } from "@dana/Types/ApiModels";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Website } from "@insite/client-framework/Services/WebsiteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";

import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useRef } from "react";
import { css } from "styled-components";
import { InvoiceExcelDownloadButton } from "@dana/Components/ExcelDownloadButton/InvoiceExcelDownloadButton";
import { NAInvoiceExcelDownloadButton } from "@dana/Components/ExcelDownloadButton/NAInvoiceExcelDownloadButton";

// --- Props Interface ---
interface InvoiceProps {
    invoiceData: InvoiceDetailsResponse | null;
    websiteData?: Website;
    selectedRegion: string | undefined;
    salesOrgDetails?: SalseOrgDetails;
}

// --- Styles Interfaces and Objects ---
export interface InvoiceStyles {
    invoiceContainer?: InjectableCss;
}

export const InvoiceStyles: InvoiceStyles = {
    invoiceContainer: {
        css: css`
            font-size: ${fontSizes.small};
            line-height: ${lineHeights.small};
            .invoice-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 16px;
                padding: 16px 0;
            }
            table {
                width: 100%;

                .price-cell {
                    text-align: right;
                }

                .align-right {
                    text-align: right;
                }

                .align-left {
                    text-align: left;
                }

                .align-center {
                    text-align: center;
                }

                .break-spaces {
                    white-space: break-spaces;
                }
            }

            td.company-logo {
                width: 200px;
            }

            td.reprint-text {
                width: 180px;
                text-align: right;
                vertical-align: top;
            }
            
            .address,
            address {
                font-style: normal;
            }

            .addresses-table {
                margin-bottom: 24px;
            }

            .company-address {
                h3 {
                    font-weight: ${fontWeights.semiBold};
                }
            }

            .printtype-text {
                font-size: ${fontSizes.large};
                line-height: ${lineHeights.large};
                font-weight: ${fontWeights.semiBold};
                color: ${themeColors.primary.danaBlue};
                text-transform: uppercase;
                margin-bottom: 16px;
            }

            .print-text {
                font-weight: ${fontWeights.semiBold};
                text-transform: uppercase;
            }

            .header-table {
                margin-bottom: 24px;
            }

            .tracking-package-table,
            .bol-no {
                margin-bottom: 16px;
            }

            .remit-wrapper {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin: 24px 0;

                .spacer {
                    width: 50px;
                }
            }
        `,
    },
};

const styles = InvoiceStyles;

// --- Component Definition ---
const Invoice: FC<InvoiceProps> = ({ invoiceData, websiteData, selectedRegion, salesOrgDetails }) => {
    const modalContentRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLTableElement>(null);
    const totalPrice = invoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.netAmount ?? 0), 0) ?? 0;
    const totalQty = invoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.quantityOrdered ?? 0), 0) ?? 0;
    const totalDiscount = invoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.discount ?? 0), 0) ?? 0;
    const footerstate = siteMessage("Invoice_FooterStatement");
    const ifni = invoiceData?.invoiceInfo ?? [];
    let totalCertiCost = 0;
    if (selectedRegion === EMEARegion) {
        totalCertiCost = ifni.reduce((sum, inv) => {
            const parts = Array.isArray(inv.partPriceInfo) ? inv.partPriceInfo : [];

            const certCostSumForLine = parts
                .filter(
                    p =>
                        String(p?.priceType ?? "")
                            .trim()
                            .toLowerCase() === "3.1 certification",
                )
                .reduce((s, p) => s + (Number(p?.amount) || 0), 0);

            return sum + certCostSumForLine;
        }, 0);
    }

    const totalTariff = ifni.reduce((sum, inv) => {
        const qty = Number(inv?.quantityShipped) || 0;
        const parts = Array.isArray(inv.partPriceInfo) ? inv.partPriceInfo : [];

        const tariffSumForLine = parts
            .filter(
                p =>
                    String(p?.priceType ?? "")
                        .trim()
                        .toLowerCase() === "tariff",
            )
            .reduce((s, p) => s + (Number(p?.amount) || 0) * qty, 0);

        return sum + tariffSumForLine;
    }, 0);

    const totalCoreFee = ifni.reduce((sum, inv) => {
        const qty = Number(inv?.quantityOrdered) || 0;
        const parts = Array.isArray(inv.partPriceInfo) ? inv.partPriceInfo : [];

        const coreSumForLine = parts
            .filter(
                p =>
                    String(p?.priceType ?? "")
                        .trim()
                        .toLowerCase() === "core fee",
            )
            .reduce((s, p) => s + (Number(p?.amount) || 0) * qty, 0);

        return sum + coreSumForLine;
    }, 0);

    const trackNumber = invoiceData?.invoiceInfo?.find(item => item.trackingNumber)?.trackingNumber ?? null;

    const totalAmt = totalPrice + totalTariff + totalCoreFee + totalCertiCost + (invoiceData?.freightAmount || 0) - totalDiscount;
    return (
        <StyledWrapper {...styles.invoiceContainer} ref={modalContentRef}>
            <div className="invoice-actions text-right">
                {selectedRegion === EMEARegion && (
                    <InvoiceExcelDownloadButton
                        invoice={invoiceData}
                        totalAmt={totalAmt}
                        totalDiscount={totalDiscount}
                        totalCertiCost={totalCertiCost}
                        totalPrice={totalPrice}
                        fileName="emea-invoice"
                        sheetName="invoice"
                        selectedRegion={selectedRegion}
                    />)}
                {selectedRegion === NARegion && (
                    <NAInvoiceExcelDownloadButton
                        invoice={invoiceData}
                        totalAmt={totalAmt}
                        totalDiscount={totalDiscount}
                        totalTariff={totalTariff}
                        totalCoreFee={totalCoreFee}
                        totalPrice={totalPrice}
                        fileName="na-invoice"
                        sheetName="invoice"
                        selectedRegion={selectedRegion}
                    />)}
                {invoiceData !== null && (
                    <InvoiceRecepients invoicetype="invoice" invoiceData={invoiceData} contentRef={modalContentRef} />
                )}
                <PrintButton label={translate("Print Invoice")} targetRef={modalContentRef} />
            </div>

            {/* Header */}
            <div ref={containerRef}>
                <table className="header-table" >
                    <tbody>
                        <tr>
                            <td className="company-logo" valign="top">
                                <img src={websiteData?.companyLogoImagePath} alt="Company Logo" className="company-logo" />
                            </td>
                            <td className="company-address" valign="top">
                                <address>
                                    <h3>{invoiceData?.shipPlantName}</h3>
                                    <p>
                                        {invoiceData?.shipPlantAddress1}
                                        {invoiceData?.shipPlantCountry && (
                                            <>
                                                <br />
                                                {invoiceData?.shipPlantCountry}
                                            </>
                                        )}
                                        {websiteData?.companyUrl && (
                                            <>
                                                <br />
                                                {websiteData.companyUrl}
                                            </>
                                        )}
                                    </p>
                                </address>
                            </td>
                            <td>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td className="font-semibold">{translate("Customer No.")} </td>
                                            <td>{invoiceData?.mailTo}</td>
                                        </tr>
                                        <tr>
                                            <td className="font-semibold">{translate("Ship To No")}</td>
                                            <td>{invoiceData?.shipTo}</td>
                                        </tr>

                                        {(selectedRegion !== EMEARegion) && (
                                            <tr>
                                                <td className="font-semibold">{translate("Order No.")}</td>
                                                <td>{invoiceData?.documentNumber}</td>
                                            </tr>
                                        )}

                                        <tr>
                                            <td className="font-semibold">{translate("Invoice No")}</td>
                                            <td>{invoiceData?.invoiceNumber}</td>
                                        </tr>

                                        {(selectedRegion !== EMEARegion) && (
                                            <tr>
                                                <td className="font-semibold">{translate("Dealer PO")}</td>
                                                <td>{invoiceData?.secondaryPO}</td>
                                            </tr>
                                        )}
                                        {(selectedRegion !== EMEARegion) && (
                                            <tr>
                                                <td className="font-semibold">{translate("Dealer Code")}</td>
                                                <td></td>
                                            </tr>
                                        )}
                                        {(selectedRegion !== NARegion && selectedRegion !== EMEARegion) && (
                                            <tr>
                                                <td className="font-semibold">{translate("Tracking No")}</td>
                                                <td>{trackNumber}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </td>
                            <td className="reprint-text">
                                <Typography forwardAs="p" className="printtype-text">
                                    {translate(" Invoice")}
                                </Typography>
                                <Typography forwardAs="p" className="print-text">
                                    {translate("Reprint")}
                                </Typography>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {selectedRegion === NARegion && (invoiceData?.forwardingAgentName || trackNumber) && (
                    <table className="tracking-package-table">
                        <thead className="align-left">
                            <tr>
                                <th>{translate("Track Package")}:</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {invoiceData?.forwardingAgentName && (
                                        <div>
                                            <span className="font-semibold">{translate("Carrier")}: </span>
                                            {invoiceData?.forwardingAgentName}
                                        </div>
                                    )}
                                    {trackNumber && (
                                        <div>
                                            <span className="font-semibold">{translate("Tracking Number")}: </span>
                                            {trackNumber}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                )}

                <table>
                    <tbody>
                        <tr>
                            <td>
                                <StyledWrapper className="bol-no">
                                    <p>
                                        <strong>{translate("Shipment / BOL No.")}:</strong> {invoiceData?.deliveryNumber}
                                    </p>
                                </StyledWrapper>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <table className="addresses-table">
                    <thead className="align-left">
                        <tr>
                            <th>{translate("Sold To")}:</th>
                            <th>{translate("Ship To")}:</th>
                            <th>{translate("Shipping Plant Address")}:</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <address>
                                    {invoiceData?.mailName}
                                    {invoiceData?.mailAddress1 && (
                                        <>
                                            <br />
                                            {invoiceData.mailAddress1}
                                        </>
                                    )}
                                    {(
                                        invoiceData?.mailCity ||
                                        invoiceData?.mailState ||
                                        invoiceData?.mailZip
                                    ) && (
                                            <>
                                                <br />
                                                {[
                                                    invoiceData?.mailCity,
                                                    invoiceData?.mailState,
                                                    invoiceData?.mailZip,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            </>
                                        )}
                                </address>
                            </td>
                            <td>
                                <address>
                                    {invoiceData?.shipName}
                                    {invoiceData?.shipAddress1 && (
                                        <>
                                            <br />
                                            {invoiceData.shipAddress1}
                                        </>
                                    )}
                                    {(
                                        invoiceData?.shipCity ||
                                        invoiceData?.shipState ||
                                        invoiceData?.shipZip
                                    ) && (
                                            <>
                                                <br />
                                                {[
                                                    invoiceData?.shipCity,
                                                    invoiceData?.shipState,
                                                    invoiceData?.shipZip,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            </>
                                        )}
                                </address>
                            </td>
                            <td>
                                <address>
                                    {invoiceData?.shipPlantName}
                                    {invoiceData?.shipPlantAddress1 && (
                                        <>
                                            <br />
                                            {invoiceData.shipPlantAddress1}
                                        </>
                                    )}
                                    {invoiceData?.shipPlantCountry && (
                                        <>
                                            <br />
                                            {invoiceData.shipPlantCountry}
                                        </>
                                    )}
                                </address>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <table className="invoice-info-table">
                    <thead className="align-left">
                        <tr>
                            <th>{translate("Invoice Date")}</th>
                            {(selectedRegion !== EMEARegion) && (<th>{translate("Customer PO")}</th>)}
                            {(selectedRegion !== EMEARegion) && (<th>{translate("Payment Terms")}</th>)}
                            {(selectedRegion !== NARegion && selectedRegion !== EMEARegion) && (
                                <th>{translate("Carrier")}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                {formatDateWithDash(selectedRegion === EMEARegion, invoiceData?.invoiceDate?.toString()?.split('T')[0] || "")}
                            </td>
                            {(selectedRegion !== EMEARegion) && (<td>{invoiceData?.poNumber}</td>)}
                            {(selectedRegion !== EMEARegion) && (<td>{invoiceData?.termsDescription}</td>)}
                            {(selectedRegion !== NARegion && selectedRegion !== EMEARegion) && (
                                <td>{invoiceData?.forwardingAgentName}</td>
                            )}
                        </tr>
                    </tbody>
                </table>

                {/* Line Item Table */}
                <StyledTable>
                    <thead>
                        <tr>
                            <th>{translate("Part No.")}</th>
                            <th>{translate("Part Description")}</th>
                            {(selectedRegion !== EMEARegion) && (
                                <th>{translate("Qty Ordered")}</th>
                            )}
                            <th>{translate("Qty Shipped")}</th>
                            <th>
                                {translate("Unit Price")} ({invoiceData?.currency})
                            </th>
                            {(selectedRegion === EMEARegion) && (
                                <th>
                                    {translate("3.1 Certificate Cost")} ({invoiceData?.currency})
                                </th>
                            )}
                            {(selectedRegion !== EMEARegion) && (
                                <>
                                    <th>
                                        {translate("Unit Tariff Fee")} ({invoiceData?.currency})
                                    </th>
                                    <th>
                                        {translate("Unit Core Deposit")} ({invoiceData?.currency})
                                    </th>
                                </>
                            )}
                            <th>
                                {translate("Extended Price")} ({invoiceData?.currency})
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoiceData?.invoiceInfo?.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    <div>
                                        <p>{item.partNumber}</p>
                                        {item.customerPartNumber && (
                                            <p>
                                               ({translate("Cust. Part No")}:{item.customerPartNumber})
                                            </p>
                                        )}
                                        <p>
                                            {translate("Customer PO")}:{item.poNumber}
                                        </p>
                                        <p>
                                            {translate("Order Number")}:{invoiceData?.documentNumber}
                                        </p>
                                    </div>
                                </td>
                                <td className="break-spaces">{item.partDescription}</td>
                                {(selectedRegion !== EMEARegion) && (
                                    <td className="price-cell">{item.quantityOrdered}</td>
                                )}
                                <td className="price-cell">{item.quantityShipped}</td>
                                <td className="price-cell">{selectedRegion === EMEARegion ? formatDecimal(item.pricePerUnit, { locale: "fr-FR" }) : formatDecimal(item.pricePerUnit)}</td>
                                {(selectedRegion === EMEARegion) && (
                                    <td className="price-cell"> {formatDecimal(item.partPriceInfo?.find(p => p.priceType === "3.1 Certification")?.amount ?? 0, { locale: "fr-FR" })}</td>
                                )}
                                {(selectedRegion !== EMEARegion) && (
                                    <>
                                        <td className="price-cell">
                                            {formatDecimal(item.partPriceInfo?.find(p => p.priceType === "Tariff")?.amount ?? 0)}
                                        </td>
                                        <td className="price-cell">
                                            {formatDecimal(item.partPriceInfo?.find(p => p.priceType === "Core Fee")?.amount ?? 0)}
                                        </td>
                                    </>
                                )}
                                <td className="price-cell">
                                    {selectedRegion === EMEARegion ? formatDecimal(
                                        (item.netAmount ?? 0) +
                                        (item.partPriceInfo?.find(p => p.priceType === "Tariff")?.amount ?? 0) *
                                        (item.quantityShipped ?? 0) +
                                        (item.partPriceInfo?.find(p => p.priceType === "Core Fee")?.amount ?? 0) *
                                        (item.quantityOrdered ?? 0)
                                        , { locale: "fr-FR" }) : formatDecimal(
                                            (item.netAmount ?? 0) +
                                            (item.partPriceInfo?.find(p => p.priceType === "Tariff")?.amount ?? 0) *
                                            (item.quantityShipped ?? 0) +
                                            (item.partPriceInfo?.find(p => p.priceType === "Core Fee")?.amount ?? 0) *
                                            (item.quantityOrdered ?? 0)
                                        )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </StyledTable>

                <StyledWrapper className="remit-wrapper">
                    <StyledWrapper>
                        <p>
                            <strong>{translate("Remit To")}:</strong>
                        </p>
                        <address>
                            {invoiceData?.remitToName}
                            {invoiceData?.remitToAddress1 && (
                                <>
                                    <br />
                                    {invoiceData.remitToAddress1}
                                </>
                            )}
                            {(
                                invoiceData?.remitToCity ||
                                invoiceData?.remitToState ||
                                invoiceData?.remitToZip
                            ) && (
                                    <>
                                        <br />
                                        {[
                                            invoiceData?.remitToCity,
                                            invoiceData?.remitToState,
                                            invoiceData?.remitToZip,
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    </>
                                )}

                        </address>
                    </StyledWrapper>
                    <StyledWrapper>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="font-semibold">
                                        {translate("Item Total")} ({invoiceData?.currency}):
                                    </td>
                                    <td className="spacer"></td>
                                    <td align="right">{selectedRegion === EMEARegion ? formatDecimal(totalPrice, { locale: "fr-FR" }) : formatDecimal(totalPrice)}</td>
                                </tr>
                                {(selectedRegion !== EMEARegion) && (
                                    <>
                                        <tr>
                                            <td className="font-semibold">
                                                {translate("Tariff Fee Total")}({invoiceData?.currency}):
                                            </td>
                                            <td className="spacer"></td>
                                            <td align="right">{formatDecimal(totalTariff)}</td>
                                        </tr>
                                        <tr>
                                            <td className="font-semibold">
                                                {translate("Core Deposit Total")}({invoiceData?.currency}):
                                            </td>
                                            <td className="spacer"></td>
                                            <td align="right">{formatDecimal(totalCoreFee)}</td>
                                        </tr>
                                    </>
                                )}
                                <tr>
                                    <td className="font-semibold">
                                        {translate("Special Discount")} ({invoiceData?.currency}):
                                    </td>
                                    <td className="spacer"></td>
                                    <td align="right">-{selectedRegion === EMEARegion ? formatDecimal(totalDiscount, { locale: "fr-FR" }) : formatDecimal(totalDiscount)}</td>
                                </tr>

                                <tr>
                                    <td className="font-semibold">
                                        {translate("Freight Charges")} ({invoiceData?.currency}):
                                    </td>
                                    <td className="spacer"></td>
                                    <td align="right">{selectedRegion === EMEARegion ? formatDecimal(invoiceData?.freightAmount ?? 0, { locale: "fr-FR" }) : formatDecimal(invoiceData?.freightAmount ?? 0)}</td>
                                </tr>
                                {(selectedRegion === EMEARegion) && (
                                    <>
                                        <tr>
                                            <td className="font-semibold">
                                                {translate("Total 3.1 Certificate Cost")} ({invoiceData?.currency}):
                                            </td>
                                            <td className="spacer"></td>
                                            <td align="right">{formatDecimal(totalCertiCost ?? 0, { locale: "fr-FR" })}</td>
                                        </tr>
                                    </>
                                )}
                                <tr>
                                    <td className="font-semibold">
                                        {translate("Total")} ({invoiceData?.currency}):
                                    </td>
                                    <td className="spacer"></td>
                                    <td className="font-semibold" align="right">
                                        {selectedRegion === EMEARegion ? formatDecimal(totalAmt, { locale: "fr-FR" }) : formatDecimal(totalAmt)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper>
                    <p> {footerstate}</p>
                </StyledWrapper>
            </div>
        </StyledWrapper>
    );
};

export default Invoice;
